import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Notification from '../Notification';

interface Student {
  id: string;
  name: string;
  email: string | null;
  studio_id: string;
}

interface ProductDescription {
  id: string;
  description: string;
}

interface OneTimeCashPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  productDescriptions: ProductDescription[];
  getAuthToken: () => string | null;
  onSuccess?: (message: string) => void;
}

export default function OneTimeCashPaymentModal({
  isOpen,
  onClose,
  student,
  productDescriptions,
  getAuthToken,
  onSuccess,
}: OneTimeCashPaymentModalProps) {
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    product_description_id: '',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!student) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Calculate period_end (one day after payment_date for one-time payments)
      const paymentDate = new Date(formData.payment_date);
      const periodEnd = new Date(paymentDate);
      periodEnd.setDate(periodEnd.getDate() + 1);
      
      // Create payment record
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manual-payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          student_id: student.id,
          studio_id: student.studio_id,
          amount: parseFloat(formData.amount),
          payment_date: formData.payment_date,
          period_start: formData.payment_date,
          period_end: periodEnd.toISOString().split('T')[0], // One day after payment date
          payment_method: formData.payment_method,
          membership_id: null, // One-time payments don't link to memberships
          product_description_id: formData.product_description_id || null,
          notes: formData.notes || null,
          is_one_time: true, // Flag to prevent billing mode change
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to record payment');
      }

      const paymentData = await response.json();

      // Send email receipt if student has an email
      let emailSent = false;
      if (student.email) {
        try {
          const emailResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manual-payments/send-receipt`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({
              payment_ids: [paymentData.id],
              student_id: student.id,
              student_email: student.email,
            }),
          });
          emailSent = emailResponse.ok;
        } catch (emailError) {
          console.error('Failed to send receipt email:', emailError);
          // Don't fail the whole operation if email fails
        }
      }

      // Show success notification
      const successMessage = student.email && emailSent
        ? `Payment of $${formData.amount} recorded successfully and email receipt sent to ${student.email}!`
        : student.email
        ? `Payment of $${formData.amount} recorded successfully! (Email receipt failed to send)`
        : `Payment of $${formData.amount} recorded successfully! (No email on file)`;
      
      if (onSuccess) {
        onSuccess(successMessage);
      }

      // Reset form and close modal
      setFormData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        product_description_id: '',
        notes: '',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !student) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            One-Time Cash Payment
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Student
            </label>
            <input
              type="text"
              value={student.name}
              disabled
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Amount *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              required
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Payment Date *
            </label>
            <input
              type="date"
              required
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Payment Method *
            </label>
            <select
              required
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="apple_pay">Apple Pay</option>
              <option value="google_pay">Google Pay</option>
              <option value="venmo">Venmo</option>
              <option value="paypal">PayPal</option>
              <option value="cashapp">Cash App</option>
              <option value="zelle">Zelle</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Product/Item dropdown hidden
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Product/Item (Optional)
            </label>
            <select
              value={formData.product_description_id}
              onChange={(e) => setFormData({ ...formData, product_description_id: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">None</option>
              {productDescriptions.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.description}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Select a product/item this payment is for (e.g., uniform, equipment)
            </p>
          </div>
          */}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes or Custom Description
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Add any notes or custom description about this payment..."
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex-shrink-0">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Processing...' : 'Record Payment & Send Receipt'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

